import React from "react";
import { useHistory } from "react-router-dom";

import { Heading, Text, Button, Image, Stack, VStack } from "@chakra-ui/react";

import useTranslations from "../hooks/useTranslations";

import notFoundImage from "../../../assets/images/errors/404.png";
import unexpectedErrorImage from "../../../assets/images/errors/unknown-error.png";
import { useIntercom } from "react-use-intercom";

type ErrorScreenType = {
  type: "PageNotFound" | "UnexpectedError";
};

const ErrorScreen: React.FC<ErrorScreenType> = ({ type }) => {
  const t = useTranslations();

  switch (type) {
    case "PageNotFound":
      return (
        <ErrorScreenContent
          title={t("errors.notFound.title") as string}
          description={t("errors.notFound.description") as string}
          showCustomerServiceButton={false}
          image={notFoundImage}
        />
      );

    case "UnexpectedError":
      return (
        <ErrorScreenContent
          title={t("errors.unexpectedError.title") as string}
          description={t("errors.unexpectedError.description") as string}
          showCustomerServiceButton={true}
          image={unexpectedErrorImage}
        />
      );

    default:
      return (
        <ErrorScreenContent
          title={t("errors.notFound.title") as string}
          description={t("errors.notFound.description") as string}
          showCustomerServiceButton={false}
          image={notFoundImage}
        />
      );
  }
};

type ErrorScreenContentType = {
  title: string;
  description: string;
  image: string;
  showCustomerServiceButton: boolean;
};

const ErrorScreenContent: React.FC<ErrorScreenContentType> = ({
  title,
  description,
  image,
  showCustomerServiceButton,
}) => {
  const history = useHistory();
  const t = useTranslations();
  const { show } = useIntercom();

  return (
    <VStack
      id="errorScreen"
      py="20"
      justifyContent="center"
      spacing="6"
      flexGrow={1}
    >
      <Stack spacing="5" alignItems="center">
        <Image boxSize="sm" objectFit="scale-down" flexShrink={1} src={image} />

        <Heading size="md" textAlign="center" fontWeight="600">
          {title}
        </Heading>

        <Text textAlign="center" maxW="lg" fontWeight="400">
          {description}
        </Text>
      </Stack>

      <Stack direction={{ base: "column", md: "row" }} spacing="4">
        {showCustomerServiceButton && (
          <Button
            id="customerServiceButton"
            backgroundColor="transparent"
            borderColor="#E2E8F0"
            borderWidth="1px"
            onClick={() => show()}
          >
            {t("errors.customerServiceButton")}
          </Button>
        )}

        <Button
          id="goBackButton"
          colorScheme="primary"
          onClick={() => history.goBack()}
        >
          {t("errors.goBackButton")}
        </Button>
      </Stack>
    </VStack>
  );
};

export default ErrorScreen;
